import { inject, Injectable } from '@angular/core';
import { ChartDataService } from '@app/core/services/charts/chart-data.service';
import { ChartThemeService } from '@app/core/services/charts/chart-theme.service';
import { ChartOptions } from 'chart.js';
import { _, TranslateService } from '@ngx-translate/core';
import { DeviceService } from '@app/core/services/device/device.service';
import { LocaleNumberPipe } from '@app/shared/pipes/locale-number/locale-number.pipe';
import { ChartZoomHelpService } from '@app/core/services/charts/chart-zoom-help.service';
import { ZoomControlsAction } from '@app/core/services/charts/plugins/zoom-controls.plugin';
import { ColorManagementService } from '@app/core/services/colors/color-management.service';
import { FloatingPopupBaseChartService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-base-chart.service';
import { IBackgroundThresholdLinesOptions } from '@app/core/services/charts/plugins/background-threshold-lines.plugin';
import { MetricColorType } from '@app/core/enums/metric-color-type.enum';
import { IFloatingPopupBarChartOptions } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-bar-chart-options';

@Injectable({ providedIn: 'root' })
export class FloatingPopupBarChartService extends FloatingPopupBaseChartService {
  public translate: TranslateService = inject(TranslateService);
  public localeNumber = inject(LocaleNumberPipe);
  protected chartThemeService = inject(ChartThemeService);
  protected chartDataService = inject(ChartDataService);
  protected deviceService = inject(DeviceService);
  protected zoomHelpService = inject(ChartZoomHelpService);
  protected colorManagementService = inject(ColorManagementService);

  public generateOptions({
    chartTranslation,
    beginAtZero,
    customTooltipConfig,
    interactionsEnabled = true,
    hoverBarBackgroundEnabled = true,
    categoryAxisTicksEnabled = true,
  }: IFloatingPopupBarChartOptions): ChartOptions {
    const translatedYLabel = chartTranslation.yLabel ? this.chartElementTranslation(chartTranslation.yLabel) : null;
    const translatedXLabel = chartTranslation.xLabel ? this.chartElementTranslation(chartTranslation.xLabel) : null;
    const translatedTitleText = chartTranslation.titleText
      ? this.chartElementTranslation(chartTranslation.titleText)
      : null;
    const scrollModifierKey = this.deviceService.isMacOS() ? 'meta' : 'ctrl';

    return {
      ...this.chartThemeService.getBaseOptions(),
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          ...this.chartThemeService.getXScaleOptions(),
          type: 'category',
          bounds: 'data',
          stacked: true,
          ticks: {
            autoSkip: true,
            maxTicksLimit: 5,
            maxRotation: 0,
            color: this.chartThemeService.getColors().standardLabelColor,
            backdropColor: this.chartThemeService.getColors().transparent,
            backdropPadding: { top: 2, bottom: 2, left: 4, right: 4 },
            showLabelBackdrop: false,
          },
          title: {
            ...this.chartThemeService.getScaleTitleOptions(),
            display: !!translatedXLabel,
            text: translatedXLabel,
          },
        },
        y: {
          ...this.chartThemeService.getYScaleOptions(),
          stacked: true,
          beginAtZero,
          title: {
            ...this.chartThemeService.getScaleTitleOptions(),
            display: !!translatedYLabel,
            text: translatedYLabel,
          },
          ticks: {
            ...this.chartThemeService.getYScaleOptions().ticks,
            callback: value => this.formatChartNumber(Number(value), 0, 2),
          },
        },
      },
      elements: {
        bar: {
          borderSkipped: 'bottom',
          borderWidth: 1,
        },
      },
      plugins: {
        scrollPan: {
          display: interactionsEnabled,
          init: { minTickWidth: 0 },
        },
        hoverVerticalLine: {
          display: true,
          lineColor: this.chartThemeService.getColors().hoverLine,
          lineDash: [4, 2],
          lineWidth: 1,
        },
        hoverBarBackground: {
          display: hoverBarBackgroundEnabled,
          backgroundColor: this.chartThemeService.getColors().hoverBarBackground,
        },
        categoryAxisTicks: {
          display: categoryAxisTicksEnabled,
          axisId: 'x',
          color: this.chartThemeService.getColors().tickColor,
          lineWidth: 1,
          majorTickLength: 8,
          minorTickLength: 4,
          maxMinorTicks: 6,
          adjustMajorTicks: true,
          majorTicksLimit: 5,
        },
        zoom: {
          pan: {
            enabled: interactionsEnabled,
            mode: 'x',
          },
          zoom: {
            wheel: { enabled: interactionsEnabled, modifierKey: scrollModifierKey },
            pinch: { enabled: false },
            mode: 'x',
          },
        },
        tooltip: {
          ...this.chartThemeService.getTooltipOptions(),
          mode: 'index',
          bodySpacing: 6,
          titleSpacing: 6,
          titleMarginBottom: 6,
          displayColors: false,
          itemSort: (a, b) => b.datasetIndex - a.datasetIndex,
          ...customTooltipConfig,
        },
        legend: {
          display: false,
        },
        title: {
          display: !!translatedTitleText,
          text: translatedTitleText,
          color: this.chartThemeService.getColors().titleColor,
          padding: {
            top: 5,
            bottom: 0,
          },
          font: {
            ...this.chartThemeService.getBaseFont(),
          },
        },
        zoomControls: {
          enabled: interactionsEnabled,
          zoomStep: '10%',
          zoomInLabel: this.translate.instant(_('@@chart-zoom-controls__zoom-in')),
          zoomOutLabel: this.translate.instant(_('@@chart-zoom-controls__zoom-out')),
          resetZoomLabel: this.translate.instant(_('@@chart-zoom-controls__reset-zoom')),
          onZoomControlClick: (action, wrapper) => {
            if (action === ZoomControlsAction.Out || action === ZoomControlsAction.In) {
              this.zoomHelpService.handleZoomHelpDialog({
                marginTop: translatedTitleText ? '1.375rem' : '0.3125rem',
                ...(wrapper ? { appendTo: wrapper } : {}),
              });
            }
          },
        },
      },
      interaction: {
        mode: 'x',
        intersect: false,
      },
      hover: {
        mode: 'x',
        intersect: false,
      },
    };
  }

  /**
   * @description Builds a standard config for the `backgroundThresholdLines` plugin
   * with two sets of horizontal threshold lines: a yellow (warning) set and a red
   * (critical) set. The concrete values are provided by the caller via the getters.
   *
   * @param warningGetValue Resolves the warning (yellow) value for each data point.
   * @param criticalGetValue Resolves the critical (red) value for each data point.
   * @param overrides Optional overrides merged on top of the standard config.
   */
  public generateThresholdLinesConfig<T = unknown>(
    warningGetValue: (meta: T, index: number) => number | null,
    criticalGetValue: (meta: T, index: number) => number | null,
    overrides?: Partial<IBackgroundThresholdLinesOptions>
  ): IBackgroundThresholdLinesOptions {
    const warningColor = this.colorManagementService
      .getColorInstanceByMetricColorType(MetricColorType.Warning)
      .toRgbString();
    const criticalColor = this.colorManagementService
      .getColorInstanceByMetricColorType(MetricColorType.Critical)
      .toRgbString();

    return {
      enabled: true,
      xAxisId: 'x',
      sets: [
        {
          id: MetricColorType.Warning,
          getValue: warningGetValue as (meta: unknown, index: number) => number | null,
          datasetIndex: 0,
          yAxisId: 'y',
          color: warningColor,
          lineWidth: 1,
          lineDash: [4, 4],
        },
        {
          id: MetricColorType.Critical,
          getValue: criticalGetValue as (meta: unknown, index: number) => number | null,
          datasetIndex: 0,
          yAxisId: 'y',
          color: criticalColor,
          lineWidth: 1,
          lineDash: [4, 4],
        },
      ],
      ...overrides,
    };
  }
}
